import {
  getDownloadUrlForDocument,
  useDeleteUserDocument,
  useUserFiles,
} from "@coaching-culture/api-client";
import {
  Button,
  Circle,
  DateText,
  EmptyState,
  Flex,
  IconButton,
  LinkButton,
  Loader,
  Panel,
  Table,
  UserFlag,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { WidgetPanelHeader } from "pages/Dashboard/Widgets/WidgetPanelHeader";
import { useState } from "react";
import { FaDownload, FaFile, FaFileUpload, FaTrash } from "react-icons/fa";
import styled from "styled-components";
import { grid, GridProps } from "styled-system";
import { EditDocumentModal } from "./EditDocumentModal";
import { UploadDocumentModal } from "./UploadDocumentModal";

const Widget = styled(Panel)<GridProps>`
  ${grid};
`;

export type DocumentsWidgetProps = {
  userId: string;
  conversationId?: string;
};

export const DocumentsWidget = ({
  userId,
  conversationId,
}: DocumentsWidgetProps) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [allDocs, setAllDocs] = useState(false);
  const [editing, setEditing] = useState<null | string>(null);
  const [me] = useUser();

  const files = useUserFiles(userId);
  const deleteMut = useDeleteUserDocument();

  const download = async (fileId: string) => {
    const url = await getDownloadUrlForDocument(userId, fileId);

    window.location.replace(url);
  };

  const deleteItem = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      await deleteMut.mutateAsync({ userId, id });
    }
  };

  let data = files.data ?? [];

  if (conversationId != null && !allDocs) {
    data = data.filter((x) => x.conversation?.id === conversationId);
  }

  return (
    <Widget width={"100%"}>
      {editing && (
        <EditDocumentModal
          userId={userId}
          documentId={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {showUploadModal && (
        <UploadDocumentModal
          userId={userId}
          conversationId={conversationId}
          onClose={() => setShowUploadModal(false)}
        />
      )}
      <WidgetPanelHeader title="User documents">
        {conversationId != null && (
          <Toggle
            onChange={setAllDocs}
            value={allDocs}
            leftLabel={"This conversation"}
            rightLabel={"All"}
          />
        )}
        <Button onClick={() => setShowUploadModal(true)} icon={FaFileUpload}>
          Upload
        </Button>
      </WidgetPanelHeader>
      {files.data == null ? (
        <Loader />
      ) : (
        <ScrollableContainer maxHeight={250}>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Uploader</th>
                <th>Uploaded on</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.data.filter((x) => x.deletedOn == null).length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyState compact icon={FaFile}>
                      No documents uploaded yet.
                    </EmptyState>
                  </td>
                </tr>
              )}
              {data
                .filter((x) => x.deletedOn == null)
                .map((x) => (
                  <tr key={x.id}>
                    <td>
                      <Flex alignItems={"center"}>
                        <Circle icon={FaFile} mr={2} />
                        <LinkButton onClick={() => setEditing(x.id)}>
                          {x.name}
                        </LinkButton>
                      </Flex>
                    </td>
                    <td>
                      <UserFlag user={x.uploader} />
                    </td>
                    <td>
                      <DateText value={x.uploadedAt} />
                    </td>
                    <td>
                      <Flex justifyContent={"flex-end"} style={{ gap: 6 }}>
                        <IconButton
                          icon={FaDownload}
                          color="positive"
                          onClick={() => download(x.id)}
                        />
                        {me.id === x.uploader.id && (
                          <IconButton
                            icon={FaTrash}
                            color="danger"
                            disabled={deleteMut.isLoading}
                            onClick={() => deleteItem(x.id)}
                          />
                        )}
                      </Flex>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </ScrollableContainer>
      )}
    </Widget>
  );
};
